// Serviços de API para integração com backend real
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL || 'https://knorrleiloes.s3.us-east-2.amazonaws.com';

// Função para criptografar senha em Base64
export const encryptPassword = (password) => {
  return btoa(password);
};

// Função para buscar todos os leilões
export const fetchLeiloes = async () => {
  try {
    console.log('Buscando leilões via:', `${API}/knorr/leiloes`);
    
    const response = await axios.get(`${API}/knorr/leiloes`, {
      timeout: 35000, // 35 segundos
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    
    console.log('Leilões recebidos:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar leilões:', error);
    
    if (error.code === 'ECONNABORTED') {
      throw new Error('Timeout ao carregar leilões. A API está demorando para responder.');
    }
    
    if (error.response) {
      if (error.response.status >= 500) {
        throw new Error('Erro interno do servidor. Tente novamente em alguns momentos.');
      }
    }
    
    throw new Error('Não foi possível carregar os leilões. Verifique sua conexão e tente novamente.');
  }
};

// Função para fazer login
export const loginCliente = async (cpf, senha) => {
  try {
    // Remove formatação do CPF se necessário
    const cpfLimpo = cpf.replace(/[^\d]/g, '');
    const cpfFormatado = cpfLimpo.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    
    // Criptografa a senha em Base64
    const senhaCriptografada = encryptPassword(senha);
    
    console.log('Fazendo login para CPF:', cpfFormatado);
    
    const response = await axios.get(`${API}/knorr/login/${cpfFormatado}/${senhaCriptografada}`, {
      timeout: 10000,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    
    console.log('Resposta do login:', response.data);
    
    // Extrai o ID do cliente da resposta
    const clienteId = response.data?.id || response.data?.ID || response.data?.cliente_id || response.data?.IDCLIENTE;
    
    if (!clienteId) {
      console.error('ID do cliente não encontrado na resposta:', response.data);
      throw new Error('Erro na resposta do servidor: ID do cliente não encontrado.');
    }
    
    console.log('Cliente ID extraído:', clienteId);
    
    // Buscar dados completos do cliente
    let clienteDetalhes = {};
    try {
      clienteDetalhes = await fetchClienteDetalhes(clienteId);
      console.log('Dados completos do cliente:', clienteDetalhes);
    } catch (error) {
      console.warn('Erro ao buscar dados completos do cliente:', error);
      // Continua com dados básicos se não conseguir buscar detalhes
    }
    
    return {
      success: true,
      data: response.data,
      user: {
        cpf: cpfFormatado,
        nome: clienteDetalhes.nome || response.data?.nome || response.data?.NOME || `Cliente ${clienteId}`,
        clienteId: clienteId,
        token: response.data?.token || response.data?.Token || `token_${Date.now()}`,
        // Dados completos do cliente
        detalhes: {
          id: clienteDetalhes.id || clienteId,
          nomeCompleto: clienteDetalhes.nome || `Cliente ${clienteId}`,
          cpf: clienteDetalhes.cpfcgc || cpfFormatado,
          cnpj: '',
          email: clienteDetalhes.email || '',
          email2: '',
          endereco: clienteDetalhes.endereco || '',
          numero: clienteDetalhes.numero || '',
          bairro: clienteDetalhes.bairro || '',
          cep: clienteDetalhes.cep || '',
          cidade: clienteDetalhes.cidade || '',
          estado: '',
          telefoneResidencial: clienteDetalhes.telres || '',
          telefoneComercial: clienteDetalhes.telcom || '',
          celular1: clienteDetalhes.cel1 || '',
          celular2: clienteDetalhes.cel2 || '',
          rg: clienteDetalhes.rg || '',
          dataNascimento: clienteDetalhes.datnas || '',
          dataCadastro: '',
          mae: '',
          pai: '',
          observacoes: '',
          ativo: '',
          bloqueado: '',
          classificacao: '',
          limiteCredito: '',
          renda: clienteDetalhes.renda || '',
          profissao: clienteDetalhes.profiss || '',
          empresa: clienteDetalhes.empres || ''
        }
      }
    };
  } catch (error) {
    console.error('Erro no login:', error);
    
    if (error.response) {
      if (error.response.status === 403) {
        // Acesso bloqueado/não liberado
        const mensagem = error.response.data?.detail || 'Seu acesso ainda não foi liberado. Entre em contato com nossa equipe.';
        throw new Error(mensagem);
      } else if (error.response.status === 404 || error.response.status === 401) {
        throw new Error('CPF ou senha incorretos.');
      } else if (error.response.status >= 500) {
        throw new Error('Erro no servidor. Tente novamente em alguns momentos.');
      }
    }
    
    throw new Error('Erro ao fazer login. Verifique sua conexão e tente novamente.');
  }
};

// Função para buscar todos os leilões (ativos e inativos) - usado em faturas
export const fetchTodosLeiloes = async () => {
  try {
    console.log('Buscando todos os leilões via:', `${API}/knorr/leiloes/todos`);
    
    const response = await axios.get(`${API}/knorr/leiloes/todos`, {
      timeout: 35000,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar todos os leilões:', error);
    throw new Error('Não foi possível carregar os leilões. Verifique sua conexão e tente novamente.');
  }
};

// Função para detectar se é dispositivo móvel
const isMobileDevice = () => {
  return window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Função para obter URL da imagem do leilão (responsiva)
export const getLeilaoImageUrl = (leilaoId) => {
  const isMobile = isMobileDevice();
  const prefix = isMobile ? 'mobile' : 'desktop';
  return `${S3_BASE_URL}/${prefix}_leilao_img_${leilaoId}.png`;
};

// Função para verificar se imagem existe
export const checkImageExists = async (url) => {
  try {
    const response = await axios.head(url, { timeout: 5000 });
    return response.status === 200;
  } catch {
    return false;
  }
};

// Função para cadastrar cliente
export const cadastrarCliente = async (dadosCliente) => {
  try {
    console.log('Cadastrando cliente via:', `${API}/knorr/cadastro`);
    
    const response = await axios.post(`${API}/knorr/cadastro`, dadosCliente, {
      timeout: 15000, // 15 segundos
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    
    console.log('Cadastro realizado:', response.data);
    
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Erro no cadastro:', error);
    
    if (error.code === 'ECONNABORTED') {
      throw new Error('Timeout ao realizar cadastro. A API está demorando para responder.');
    }
    
    if (error.response) {
      if (error.response.status === 400) {
        throw new Error('Dados inválidos. Verifique se todos os campos estão preenchidos corretamente.');
      } else if (error.response.status === 409) {
        throw new Error('CPF/CNPJ ou e-mail já cadastrado. Tente fazer login ou use dados diferentes.');
      } else if (error.response.status >= 500) {
        throw new Error('Erro interno do servidor. Tente novamente em alguns momentos.');
      }
    }
    
    throw new Error('Não foi possível realizar o cadastro. Verifique sua conexão e tente novamente.');
  }
};

// Função para buscar dados completos do cliente
export const fetchClienteDetalhes = async (clienteId) => {
  try {
    console.log('Buscando detalhes do cliente via:', `${API}/knorr/clientes/${clienteId}`);
    
    const response = await axios.get(`${API}/knorr/clientes/${clienteId}`, {
      timeout: 15000, // 15 segundos
      headers: {
        'Accept': 'application/json'
      }
    });
    
    console.log('Detalhes do cliente recebidos:', response.data);
    
    // A API retorna um array, então pegamos o primeiro item
    const raw = Array.isArray(response.data) ? response.data[0] : response.data;
    
    if (!raw) return {};
    
    // Mapear campos da API Delphi para nomes padronizados
    const clienteData = {
      id: raw.id || raw.ID,
      nome: raw.nomexx || raw.nome || raw.NOME || '',
      cpfcgc: raw.cpfxxx || raw.cpfcgc || raw.CPFCGC || '',
      cnpj: raw.cnpjxx || raw.cnpj || '',
      email: raw.emailx || raw.email || raw.EMAIL || '',
      endereco: raw.endere || raw.endereco || '',
      numero: raw.numero || '',
      bairro: raw.bairro || '',
      cep: raw.cepxxx || raw.cep || '',
      cidade: raw.cidade || '',
      telres: raw.telres || '',
      telcom: raw.telcom || '',
      celular: raw.celula || raw.celular || '',
      rg: raw.rgxxxx || raw.rg || '',
      datnas: raw.datnas || '',
      datcad: raw.datcad || '',
    };
    
    return clienteData;
  } catch (error) {
    console.error('Erro ao buscar detalhes do cliente:', error);
    
    if (error.code === 'ECONNABORTED') {
      throw new Error('Timeout ao buscar dados do cliente. A API está demorando para responder.');
    }
    
    if (error.response) {
      if (error.response.status === 404) {
        throw new Error('Cliente não encontrado.');
      } else if (error.response.status >= 500) {
        throw new Error('Erro interno do servidor. Tente novamente em alguns momentos.');
      }
    }
    
    throw new Error('Não foi possível buscar os dados do cliente. Verifique sua conexão e tente novamente.');
  }
};

// Função para alterar senha do cliente
export const alterarSenhaCliente = async (clienteId, novaSenha) => {
  try {
    console.log('Alterando senha do cliente:', clienteId);
    
    const response = await axios.post(`${API}/knorr/clientes/${clienteId}/alterar-senha`, {
      clienteId: parseInt(clienteId),
      novaSenha: novaSenha
    }, {
      timeout: 15000, // 15 segundos
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    
    console.log('Senha alterada com sucesso:', response.data);
    
    return {
      success: true,
      message: response.data.message || 'Senha alterada com sucesso!'
    };
    
  } catch (error) {
    console.error('Erro ao alterar senha:', error);
    
    if (error.code === 'ECONNABORTED') {
      throw new Error('Timeout ao alterar senha. Tente novamente.');
    }
    
    if (error.response) {
      if (error.response.status === 400) {
        throw new Error('Dados inválidos para alteração de senha.');
      } else if (error.response.status === 404) {
        throw new Error('Cliente não encontrado.');
      } else if (error.response.status >= 500) {
        throw new Error('Erro interno do servidor. Tente novamente em alguns momentos.');
      } else {
        throw new Error(`Erro ao alterar senha: ${error.response.data?.detail || error.response.statusText}`);
      }
    }
    
    throw new Error('Não foi possível alterar a senha. Verifique sua conexão e tente novamente.');
  }
};

// Função para buscar cliente pelo CPF (para recuperação de senha)
export const buscarClientePorCpf = async (cpf) => {
  try {
    const cpfLimpo = cpf.replace(/[^\d]/g, '');
    console.log('Buscando cliente pelo CPF:', cpfLimpo);
    
    const response = await axios.get(`${API}/knorr/clientes/buscar-por-cpf/${cpfLimpo}`, {
      timeout: 15000,
      headers: {
        'Accept': 'application/json'
      }
    });
    
    console.log('Cliente encontrado:', response.data);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar cliente por CPF:', error);
    
    if (error.response?.status === 404) {
      throw new Error('Cliente não encontrado. Verifique o CPF informado.');
    }
    
    throw new Error('Erro ao buscar cliente. Tente novamente.');
  }
};

// Função para buscar lances do cliente
export const fetchClienteLances = async (clienteId) => {
  try {
    console.log('Buscando lances do cliente via:', `${API}/knorr/clientes/${clienteId}/lances`);
    
    const response = await axios.get(`${API}/knorr/clientes/${clienteId}/lances`, {
      timeout: 15000, // 15 segundos
      headers: {
        'Accept': 'application/json'
      }
    });
    
    console.log('Lances do cliente recebidos:', response.data);
    
    return response.data || [];
  } catch (error) {
    console.error('Erro ao buscar lances do cliente:', error);
    
    if (error.code === 'ECONNABORTED') {
      throw new Error('Timeout ao buscar lances. A API está demorando para responder.');
    }
    
    if (error.response) {
      if (error.response.status === 404) {
        // Cliente não tem lances ainda
        return [];
      } else if (error.response.status >= 500) {
        throw new Error('Erro interno do servidor. Tente novamente em alguns momentos.');
      }
    }
    
    throw new Error('Não foi possível buscar os lances. Verifique sua conexão e tente novamente.');
  }
};